import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Abrufausfall, useAbruf } from './Abruf'
import type { Abruf } from './Abruf'
import { api, Betriebsstoerung } from './api'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { Dialogfenster } from './Dialogfenster'
import type {
  Datenquelle,
  Auswahl,
  Bericht,
  Freigabeurteil,
  Importlaufkopf,
  Messwerte,
  Nachweispruefung,
  Rechenantwort,
  Vorgangskopf,
} from './typen'
import type { Raster } from './Zeitraum'
import { bezuegeIn, istFehler, istAuswertungsfehler, lies, werteAus } from './Formel'
import type { Bezugsraum, Formelbefund } from './Formel'
import {
  BREITEN,
  gegenstaendeFuer,
  groessenFuer,
  artenFuer,
  entferne,
  findeGroesse,
  ersetze,
  frueher,
  fuegeEin,
  kachelraum,
  liesBrett,
  neueKachel,
  neueKennung,
  speichereBrett,
  verdopple,
  vergissBrett,
  verschiebe,
  verlaufspunkte,
  vorlage,
  ziehe,
} from './Kacheln'
import type {
  Brett,
  Breite,
  Einheitsart,
  Gegenstand,
  Kachel,
  Kachelart,
  Kachelbezug,
  Kachelraum,
  Sprung,
} from './Kacheln'
import {
  Kachelbefundzeile,
  Standkachel,
  Verlaufkachel,
  Zahlkachel,
  lieferzeilen,
  schrittzeilen,
  systemzeilen,
} from './Kachelinhalt'
import type { Verlauf } from './Kachelinhalt'
import { beschriftung, bewertungsebenenname, kreisname, periodenname, positionsbezeichnung, t } from './sprache'
import { Nachfrage } from './Nachfrage'
import { Pruefregeln } from './Pruefregeln'
import type { Sprachschluessel } from './sprache'

/**
 * Das Kachelbrett — die Startseite (7.5, R337–R349).
 *
 * **Die Arbeitsliste steht oben und lässt sich nicht entfernen** (R337). Sie
 * kommt als fertiges Stück von außen herein; dieses Bauteil rahmt sie ein und
 * setzt darunter, was der Anwender sich selbst zusammengestellt hat. R140
 * bleibt damit wörtlich erfüllt: Wer sich anmeldet, sieht seinen Arbeitsvorrat.
 *
 * **Ansehen und Gestalten sind zwei Zustände** (R344). Im Ansehen ist jede
 * Kachel ein Sprung; erst im Gestalten wird sie ein Griff. Wer eine Zahl
 * anklicken will, soll sie nicht versehentlich verschieben — dieselbe
 * Entscheidung wie am Zeitstreifen und am Gliederungsbaum.
 *
 * **Gerechnet wird hier nichts** (R349): Das Brett liest, was die Kontextzeile
 * ohnehin geholt hat. Nur eine festgezurrte Herkunft kostet einen eigenen
 * Abruf — und zwar einen je Herkunft, nicht einen je Kachel.
 */

export type Brettquellen = {
  lauf: Rechenantwort | null
  urteil: Freigabeurteil | null
  bilanz: Bericht | null
  guv: Bericht | null
  pruefung: Nachweispruefung | null
  messung: Messwerte | null
  vorgaenge: Vorgangskopf[]
  /** Die Datenquellen — für die Zeile „offene Zielfelder" im Systemzustand (R523). */
  datenquellen: Datenquelle[] | null
}

export type Brettkontext = {
  kreis: string
  ledger: string
  periode: number
  raster: Raster
  /**
   * Wie Kreis und Bewertungsebene **heißen** (R705).
   *
   * **Die dreizehnte Stelle.** Die Zusammenführung vom 2026-09-22 hat ein
   * Dutzend Bildschirme auf eine gemeinsame Entscheidung gezogen — diese hier
   * hat sie übersehen, weil die Herkunftszeile ihren Text aus zwei
   * Schlüsseln selbst zusammensetzt und niemanden fragt: *„· LDG-HGB ·
   * Dezember 2026"*. Gefunden beim Durchgang durch die Bildschirme, nicht
   * beim Bauen.
   */
  namen?: Record<string, string>
}

/**
 * **Das Zahlenfeld der Verlaufskachel heißt nach dem Raster** — *Monate*,
 * *Quartale*, *Jahre*. Es zählt in genau diesen Abschnitten, also steht das
 * Wort daran und nicht „Einheiten der Zeitachse": „Einheit" trug am Bildschirm
 * bis zum 2026-09-17 die Gesellschaft. Anders als der Feldname in R430 darf
 * dieser mitwandern, weil die Zahl daneben wirklich Monate zählt.
 */
const ABSCHNITTE: Record<Raster, Sprachschluessel> = {
  monat: 'kachel.feld.einheiten.monat',
  quartal: 'kachel.feld.einheiten.quartal',
  jahr: 'kachel.feld.einheiten.jahr',
}

export function Kachelbrett({
  erste,
  rolle,
  quellen,
  kontext,
  auswahl,
  laufperioden,
  aufSprung,
  aufNachweis,
}: {
  /**
   * Die erste Kachel — fertig von außen, nicht entfernbar (R337), und
   * **welche es ist, sagt die Rolle** (R353): beim Prüfer die freigegebenen
   * Abschlüsse, bei allen anderen die Arbeitsliste.
   */
  erste: ReactNode
  /** Die gelesene Rolle (R351) — sie bestimmt Vorlage und Katalog. */
  rolle: string
  quellen: Brettquellen
  kontext: Brettkontext
  auswahl: Auswahl | null
  laufperioden: number[]
  aufSprung: (ziel: Sprung) => void
  aufNachweis: (bezug: Kachelbezug, raum: Kachelraum) => void
}) {
  const [brett, setzeBrett] = useState<Brett>(() => liesBrett(rolle))
  const [bearbeiten, setBearbeiten] = useState(false)
  const [katalog, setKatalog] = useState(false)
  /** Ob die Frage vor dem Zurücksetzen offensteht (R394). */
  const [zuruecksetzen, setZuruecksetzen] = useState(false)
  /** Ob das Fenster „Prüfregeln festlegen" offensteht (R910). */
  const [pruefregeln, setPruefregeln] = useState(false)
  const [blattFuer, setBlattFuer] = useState<string | null>(null)
  const [gezogen, setGezogen] = useState<string | null>(null)

  // Jede Änderung wird sofort gemerkt. Kein „Speichern"-Knopf: Was der
  // Anwender an seinem eigenen Brett verschiebt, ist keine Eingabe, die
  // jemand bestätigen müsste (R346).
  const merke = useCallback((neu: Brett) => {
    setzeBrett(neu)
    speichereBrett(neu)
  }, [])

  const { raeume, gestoert } = useRaeume(brett.kacheln, quellen, kontext)
  const verlaeufe = useVerlaeufe(brett.kacheln, kontext)
  const lieferungen = useLieferungen(brett.kacheln)

  const blatt = brett.kacheln.find((k) => k.id === blattFuer) ?? null

  return (
    <section className="kachelbrett">
      {/*
        **Steht eines der Fenster offen, ist das Brett dahinter nicht bedienbar**
        (R451). Der Schatten nimmt nur der Maus den Zugriff; die Tabulatortaste
        lief weiter auf Knöpfe, die der Anwender in diesem Augenblick nicht
        sieht — und beim Gestalten sind das zehn.
      */}
      <div inert={zuruecksetzen || katalog || blatt || pruefregeln ? '' : undefined}>
        <div className="brettleiste">
          <button type="button" onClick={() => setBearbeiten((a) => !a)}>
            {bearbeiten ? t('kachel.brett.fertig') : t('kachel.brett.gestalten')}
          </button>

          {/*
            **Wie streng die Freigabe prüft** (2.13 Abschnitt 13, R910) — neben
            dem Brett und nicht in einem Einstellungsbereich: Hier steht die
            Arbeitsliste, und sie zeigt, was die Regeln daraus machen.
          */}
          {!bearbeiten && (
            <button type="button" onClick={() => setPruefregeln(true)}>
              {t('pruefregeln.titel')}
            </button>
          )}

          {bearbeiten && (
            <>
              <button type="button" className="haupt" onClick={() => setKatalog(true)}>
                {t('kachel.hinzufuegen')}
              </button>
              {/*
                **Das Programm fragt selbst** (R394). `window.confirm` gab in
                dieser Hülle stillschweigend `false` zurück — der Knopf sah aus
                wie ein Knopf und tat nichts.
              */}
              <button type="button" onClick={() => setZuruecksetzen(true)}>
                {t('kachel.zuruecksetzen')}
              </button>
              <span className="hinweis">{t('kachel.brett.gestalten.hinweis')}</span>
            </>
          )}
        </div>

        {/*
          Die Arbeitsliste in voller Breite, an erster Stelle, ohne Handgriffe
          (R337). Sie trägt denselben Rahmen wie jede andere Kachel — **eine
          Stelle, keine zweite Sorte**: Was hier steht, ist eine Kachel, und dass
          man sie nicht entfernen kann, ist eine Eigenschaft und kein Sonderfall
          in der Zeichnung.
        */}
        <div className="kachelraster">
          <article className="kachel fest" style={{ gridColumn: 'span 12' }}>
            {erste}
          </article>

          {brett.kacheln.map((kachel, stelle) => (
            <Kachelrahmen
              key={kachel.id}
              kachel={kachel}
              istErste={stelle === 0}
              istLetzte={stelle === brett.kacheln.length - 1}
              raum={raeume[herkunftsschluessel(kachel, kontext)] ?? null}
              bearbeiten={bearbeiten}
              gezogen={gezogen}
              aufZug={setGezogen}
              aufAblage={(auf) => {
                if (gezogen) {
                  merke(ziehe(brett, gezogen, auf))
                }

                setGezogen(null)
              }}
              aufVerschieben={(richtung) => merke(verschiebe(brett, kachel.id, richtung))
              }
              aufKlappen={() =>
                merke(ersetze(brett, { ...kachel, eingeklappt: !kachel.eingeklappt }))
              }
              aufBlatt={() => setBlattFuer(kachel.id)}
              aufVerdoppeln={() => merke(verdopple(brett, kachel.id))}
              aufEntfernen={() => merke(entferne(brett, kachel.id))}
              kontext={kontext}
            >
              <Inhalt
                kachel={kachel}
                raum={raeume[herkunftsschluessel(kachel, kontext)] ?? null}
                gestoert={gestoert.has(herkunftsschluessel(kachel, kontext))}
                verlauf={verlaeufe[kachel.id]}
                quellen={quellen}
                lieferungen={lieferungen}
                kontext={kontext}
                aufSprung={aufSprung}
                aufNachweis={aufNachweis}
              />
            </Kachelrahmen>
          ))}
        </div>

        {/*
          **Ein leeres Brett gibt es nicht** (R348): Wer alles entfernt, behält
          die Arbeitsliste — und liest darunter, wie man Kacheln hinzufügt. Eine
          Seite, die nur „nichts vorhanden" sagt, führt niemanden (R144).
        */}
        {brett.kacheln.length === 0 && (
          <p className="leerhinweis">
            {t('kachel.leer')} <span className="weg">{t('kachel.leer.weg')}</span>
          </p>
        )}
      </div>

      {zuruecksetzen && (
        <Nachfrage
          titel={t('kachel.zuruecksetzen')}
          gefahr
          frage={t('kachel.zuruecksetzen.frage')}
          hinweis={t('kachel.zuruecksetzen.hinweis')}
          bestaetigen={t('kachel.zuruecksetzen')}
          aufSchliessen={() => setZuruecksetzen(false)}
          aufJa={() => {
            setZuruecksetzen(false)
            vergissBrett()
            setzeBrett(vorlage(rolle))
          }}
        />
      )}

      {/*
        **Die Fenster über dem Brett sind das übliche Fenster** (7.2 Abschnitt
        41, R1260): Das Brett bleibt stehen, das Fenster geht zu, und der
        Anwender ist da, wo er war (R204) — und verschieben, groß schalten und
        an der Ecke ziehen kann er es jetzt auch.

        **Prüfregeln geht bildschirmfüllend auf.** Siebzehn Prüfungen in drei
        Spalten, und zwei davon geben keinen Platz ab: die drei Knöpfe der
        Strenge und „Festgelegt", das nicht umbricht. In 760 Punkten bliebe der
        Prüfung selbst ein schmaler Streifen, und jeder Satz bräche auf vier
        Zeilen um — genau dagegen war das alte Fenster 1200 Punkte breit.
      */}
      {pruefregeln && (
        <Dialogfenster titel={t('pruefregeln.titel')} weite="breit" aufSchliessen={() => setPruefregeln(false)}>
          <Pruefregeln
            periode={kontext.periode}
            perioden={[...new Set((auswahl?.perioden ?? []).map((p) => p.periode))].sort((a, b) => a - b)}
          />
        </Dialogfenster>
      )}

      {katalog && (
        <Katalog
          rolle={rolle}
          aufSchliessen={() => setKatalog(false)}
          aufWahl={(art, gegenstand) => {
            const id = neueKennung(brett)
            const neu = neueKachel(art, id, gegenstand ? { gegenstand } : {})
            merke(fuegeEin(brett, neu))
            setKatalog(false)
            // Gleich ins Blatt: Eine Kachel, die man erst suchen muss, um sie
            // einzurichten, richtet niemand ein.
            setBlattFuer(id)
          }}
        />
      )}

      {blatt && (
        <Kachelblatt
          kachel={blatt}
          rolle={rolle}
          raum={raeume[herkunftsschluessel(blatt, kontext)] ?? null}
          auswahl={auswahl}
          laufperioden={laufperioden}
          kontext={kontext}
          aufSchliessen={() => setBlattFuer(null)}
          aufUebernehmen={(neu) => {
            merke(ersetze(brett, neu))
            setBlattFuer(null)
          }}
        />
      )}
    </section>
  )
}

/* --- Der Rahmen ------------------------------------------------------------ */

function Kachelrahmen({
  kachel,
  raum,
  bearbeiten,
  gezogen,
  kontext,
  children,
  aufZug,
  aufAblage,
  aufVerschieben,
  istErste,
  istLetzte,
  aufKlappen,
  aufBlatt,
  aufVerdoppeln,
  aufEntfernen,
}: {
  kachel: Kachel
  /** Für die Beschriftung: Eine Position heißt, wie sie im Bericht heißt. */
  raum: Kachelraum | null
  bearbeiten: boolean
  gezogen: string | null
  kontext: Brettkontext
  children: ReactNode
  aufZug: (id: string | null) => void
  aufAblage: (auf: string) => void
  aufVerschieben: (richtung: 'vor' | 'zurueck') => void
  /** Ob sie ganz vorn steht — dann führt *nach vorn* nirgendwohin. */
  istErste: boolean
  /** Ob sie ganz hinten steht. */
  istLetzte: boolean
  aufKlappen: () => void
  aufBlatt: () => void
  aufVerdoppeln: () => void
  aufEntfernen: () => void
}) {
  return (
    <article
      className={`kachel ${bearbeiten ? 'griff' : ''} ${gezogen === kachel.id ? 'zieht' : ''}`}
      style={{ gridColumn: `span ${kachel.breite}`, gridRow: `span ${kachel.hoehe}` }}
      draggable={bearbeiten}
      onDragStart={() => aufZug(kachel.id)}
      onDragEnd={() => aufZug(null)}
      onDragOver={(e) => bearbeiten && e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        aufAblage(kachel.id)
      }}
    >
      <header className="kachelkopf">
        <h2>{kachel.titel || vorratsname(kachel, raum)}</h2>
        {/*
          **Jede Kachel nennt ihre Herkunft** (R345) — und ob sie der Auswahl
          oben folgt oder festgezurrt ist. Eine Zahl, bei der man nicht weiß,
          für welchen Kreis sie gilt, ist keine Auskunft, sondern eine
          Behauptung.
        */}
        <p className="herkunft">{herkunftstext(kachel, kontext)}</p>

        <div className="kachelgriffe">
          <button
            type="button"
            className="griffknopf klapper"
            onClick={aufKlappen}
            title={t(kachel.eingeklappt ? 'kachel.ausklappen' : 'kachel.einklappen')}
            aria-label={t(kachel.eingeklappt ? 'kachel.ausklappen' : 'kachel.einklappen')}
          >
            {kachel.eingeklappt ? '▸' : '▾'}
          </button>

          {/*
            Der Klickweg steht neben dem Ziehweg (R344/R306): Wer nicht ziehen
            will oder kann, ordnet mit zwei Pfeilen.

            **An den Rändern sind sie abgeschaltet.** Die erste Kachel kann nicht
            weiter nach vorn, die letzte nicht weiter nach hinten; `verschiebe`
            gibt dort das unveränderte Brett zurück. Ein Pfeil, der sich drücken
            lässt und nichts tut, ist für den Anwender ein toter Knopf (R451).
          */}
          {bearbeiten && (
            <>
              <button
                type="button"
                className="griffknopf"
                disabled={istErste}
                onClick={() => aufVerschieben('vor')}
                title={t('kachel.vor')}
                aria-label={t('kachel.vor')}
              >
                ◂
              </button>
              <button
                type="button"
                className="griffknopf"
                disabled={istLetzte}
                onClick={() => aufVerschieben('zurueck')}
                title={t('kachel.zurueck')}
                aria-label={t('kachel.zurueck')}
              >
                ▸
              </button>
              <button type="button" className="griffknopf" onClick={aufBlatt} title={t('kachel.einrichten')}>
                {t('kachel.einrichten')}
              </button>
              <button
                type="button"
                className="griffknopf"
                onClick={aufVerdoppeln}
                title={t('kachel.verdoppeln')}
                aria-label={t('kachel.verdoppeln')}
              >
                ⧉
              </button>
              <button
                type="button"
                className="griffknopf entfernen"
                onClick={aufEntfernen}
                title={t('kachel.entfernen')}
                aria-label={t('kachel.entfernen')}
              >
                ✕
              </button>
            </>
          )}
        </div>
      </header>

      {!kachel.eingeklappt && <div className="kachelleib">{children}</div>}
    </article>
  )
}

/* --- Der Inhalt ------------------------------------------------------------ */

function Inhalt({
  kachel,
  raum,
  gestoert = false,
  verlauf,
  quellen,
  lieferungen,
  kontext,
  aufSprung,
  aufNachweis,
}: {
  kachel: Kachel
  raum: Kachelraum | null
  /** Der Abruf des Raums scheiterte technisch (R1003) — dann steht kein „kein Lauf" da. */
  gestoert?: boolean
  verlauf: Verlauf | undefined
  quellen: Brettquellen
  /** Die Importläufe, wenn eine Kachel den Lieferstand zeigt — ein Hauptabruf (R1211). */
  lieferungen: Abruf<Importlaufkopf[]>
  kontext: Brettkontext
  aufSprung: (ziel: Sprung) => void
  aufNachweis: (bezug: Kachelbezug, raum: Kachelraum) => void
}) {
  if (kachel.art === 'stand') {
    const gegenstand = kachel.gegenstand ?? 'schritte'

    if (gegenstand === 'schritte') {
      return (
        <Standkachel zeilen={schrittzeilen(quellen.vorgaenge)} kopf={null} aufSprung={aufSprung} />
      )
    }

    if (gegenstand === 'lieferstand') {
      // **Ein Ausfall ist kein „nichts geliefert"** (7.2 Abschnitt 39, R1210):
      // Kamen die Läufe nicht, steht das da — nicht der Satz für leer.
      if (lieferungen.ausgefallen) {
        return <Abrufausfall abruf={lieferungen} />
      }

      if (lieferungen.wert === null) {
        return <p className="hinweis">{t('kachel.laedt')}</p>
      }

      return (
        <Standkachel zeilen={lieferzeilen(lieferungen.wert, kontext.periode)} kopf={null} aufSprung={aufSprung} />
      )
    }

    return (
      <Standkachel
        zeilen={systemzeilen(quellen.messung, quellen.datenquellen)}
        kopf={null}
        aufSprung={aufSprung}
      />
    )
  }

  if (kachel.art === 'verlauf') {
    return (
      <Verlaufkachel
        kachel={kachel}
        verlauf={verlauf ?? { art: 'laedt' }}
        aufSprung={aufSprung}
        ziel={{ bereich: 'berichte', unterpunkt: berichtsart(kachel) }}
      />
    )
  }

  // Eine Störung ist kein fehlender Lauf; sie steht im Störungsfenster
  // (7.2 Abschnitt 30, R1003).
  if (!raum && gestoert) {
    return null
  }

  if (!raum) {
    return (
      <p className="hinweis">
        {t('kachel.keinlauf')} <span className="weg">{t('kachel.keinlauf.weg')}</span>
      </p>
    )
  }

  return <Zahlkachel kachel={kachel} raum={raum} aufSprung={aufSprung} aufNachweis={aufNachweis} />
}

/* --- Der Katalog ----------------------------------------------------------- */

function Katalog({
  rolle,
  aufWahl,
  aufSchliessen,
}: {
  /**
   * **Der Katalog folgt R155 — durch Weglassen** (R354).
   *
   * Die Prüffrage ist das **Ziel**: Eine Kachel führt irgendwohin (R347);
   * führt sie in einen Bereich, den diese Rolle nicht bearbeitet, ist sie eine
   * Sackgasse mit Zahl. Das ist **keine Rechteprüfung** — die gehört an die
   * API (Bereich 8) und niemals an eine Bildschirmliste.
   */
  rolle: string
  aufWahl: (art: Kachelart, gegenstand: Gegenstand | null) => void
  aufSchliessen: () => void
}) {
  const gegenstaende = gegenstaendeFuer(rolle)

  const arten = artenFuer(rolle)

  return (
    <Dialogfenster titel={t('kachel.katalog.titel')} weite="mittel" aufSchliessen={aufSchliessen}>
      <ul className="katalog">
        {arten.map((art) => (
          <li key={art}>
            <button type="button" className="katalogeintrag" onClick={() => aufWahl(art, null)}>
              <span className="name">{t(`kachel.art.${art}` as Sprachschluessel)}</span>
              <span className="zweck">{t(`kachel.art.${art}.zweck` as Sprachschluessel)}</span>
            </button>

            {/*
              Der Gegenstand einer Standkachel ist selbst ein Vorrat (R338) —
              er steht deshalb im Katalog und nicht erst im Blatt: Wer
              „Fortschritt" wählt, will wissen, worüber.
            */}
            {art === 'stand' && gegenstaende.length > 0 && (
              <ul className="gegenstaende">
                {gegenstaende.map((g) => (
                  <li key={g}>
                    <button type="button" className="schmal" onClick={() => aufWahl('stand', g)}>
                      {t(`kachel.gegenstand.${g}` as Sprachschluessel)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </Dialogfenster>
  )
}

/* --- Das Blatt ------------------------------------------------------------- */

/**
 * Ein Blatt aus Eigenschaften — **dasselbe Muster wie in der
 * Stammdatenpflege** (R317).
 *
 * Der Lerneffekt setzt früh ein: Wer ein Konto angelegt hat, kennt dieses
 * Blatt. Links die Felder, unten *Übernehmen* und *Abbrechen*, und jede
 * Angabe sagt, was sie bedeutet, statt es vorauszusetzen.
 */
function Kachelblatt({
  kachel,
  rolle,
  raum,
  auswahl,
  laufperioden,
  kontext,
  aufUebernehmen,
  aufSchliessen,
}: {
  kachel: Kachel
  /** Auch das Blatt bietet nur an, was zur Zuständigkeit gehört (R354). */
  rolle: string
  raum: Kachelraum | null
  auswahl: Auswahl | null
  laufperioden: number[]
  kontext: Brettkontext
  aufUebernehmen: (kachel: Kachel) => void
  aufSchliessen: () => void
}) {
  const [entwurf, setEntwurf] = useState<Kachel>(kachel)
  const aendere = (teile: Partial<Kachel>): void => setEntwurf((k) => ({ ...k, ...teile }))
  const groessen = groessenFuer(rolle)
  const gegenstaende = gegenstaendeFuer(rolle)

  return (
    <Dialogfenster
      titel={t('kachel.blatt.titel')}
      weite="mittel"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          <button type="button" className="haupt" onClick={() => aufUebernehmen(entwurf)}>
            {t('kachel.uebernehmen')}
          </button>
          <button type="button" onClick={aufSchliessen}>
            {t('kachel.abbrechen')}
          </button>
        </>
      }
    >
      <div className="kachelblatt">
        <label className="feld">
          <span>{t('kachel.feld.titel')}</span>
          <input
            type="text"
            value={entwurf.titel ?? ''}
            placeholder={vorratsname(entwurf, raum)}
            onChange={(e) => aendere({ titel: e.target.value || null })}
          />
          <small>{t('kachel.feld.titel.vorgabe')}</small>
        </label>

        {entwurf.art !== 'stand' && (
          <>
            <fieldset className="feld">
              <legend>{t('kachel.feld.quelle')}</legend>
              <label className="wahl">
                <input
                  type="radio"
                  checked={entwurf.quelle.art === 'gestellt'}
                  onChange={() =>
                    aendere({ quelle: { art: 'gestellt', schluessel: groessen[0]?.schluessel ?? '' } })
                  }
                />
                {t('kachel.quelle.gestellt')}
              </label>
              <label className="wahl">
                <input
                  type="radio"
                  checked={entwurf.quelle.art === 'formel'}
                  onChange={() => aendere({ quelle: { art: 'formel', ausdruck: '' } })}
                />
                {t('kachel.quelle.formel')}
              </label>
            </fieldset>

            {entwurf.quelle.art === 'gestellt' ? (
              <label className="feld">
                <span>{t('kachel.feld.groesse')}</span>
                <Auswahlfeld
                  wert={entwurf.quelle.schluessel}
                  beschriftung={t('kachel.feld.groesse')}
                  /*
                    **Die Liste ist zur Hälfte Bestand** (R583): Auf die
                    gestellten Größen folgt jede Position beider Berichte — ein
                    Name, keine Koordinate (R340). Genau diese Hälfte wächst mit
                    dem Positionsplan des Kunden.
                  */
                  eintraege={[
                    ...groessen.map((g) => ({ wert: g.schluessel, text: t(g.schriftzug) })),
                    ...positionswahl(raum),
                  ]}
                  aufWahl={(gewaehlt) => {
                    const groesse = groessen.find((g) => g.schluessel === gewaehlt)
                    aendere({
                      quelle: { art: 'gestellt', schluessel: gewaehlt },
                      einheit: groesse?.einheit ?? bezugseinheit(gewaehlt, raum),
                      schwellen: {
                        ...entwurf.schwellen,
                        richtung: groesse?.richtung ?? entwurf.schwellen.richtung,
                      },
                    })
                  }}
                />
              </label>
            ) : (
              <Formelfeld
                ausdruck={entwurf.quelle.ausdruck}
                rolle={rolle}
                raum={raum}
                aufAusdruck={(ausdruck) => aendere({ quelle: { art: 'formel', ausdruck } })}
              />
            )}

            <label className="feld">
              <span>{t('kachel.feld.einheit')}</span>
              <select
                value={entwurf.einheit}
                onChange={(e) => aendere({ einheit: e.target.value as Einheitsart })}
              >
                <option value="betrag">{t('kachel.einheit.betrag')}</option>
                <option value="anzahl">{t('kachel.einheit.anzahl')}</option>
                <option value="prozent">{t('kachel.einheit.prozent')}</option>
                <option value="ms">{t('kachel.einheit.mswahl')}</option>
              </select>
            </label>
          </>
        )}

        {entwurf.art === 'stand' && (
          <label className="feld">
            <span>{t('kachel.katalog.titel')}</span>
            <select
              value={entwurf.gegenstand ?? 'schritte'}
              onChange={(e) => aendere({ gegenstand: e.target.value as Gegenstand })}
            >
              {gegenstaende.map((g) => (
                <option key={g} value={g}>
                  {t(`kachel.gegenstand.${g}` as Sprachschluessel)}
                </option>
              ))}
            </select>
          </label>
        )}

        {entwurf.art === 'verlauf' && (
          <>
            <label className="feld">
              <span>{t('kachel.feld.darstellung')}</span>
              <select
                value={entwurf.darstellung}
                onChange={(e) => aendere({ darstellung: e.target.value as 'balken' | 'linie' })}
              >
                <option value="balken">{t('kachel.darstellung.balken')}</option>
                <option value="linie">{t('kachel.darstellung.linie')}</option>
              </select>
            </label>

            <label className="feld">
              <span>{t(ABSCHNITTE[kontext.raster])}</span>
              <input
                type="number"
                min={2}
                max={48}
                value={entwurf.einheiten}
                onChange={(e) => aendere({ einheiten: Number(e.target.value) || 12 })}
              />
            </label>
          </>
        )}

        {/*
          **Die Ampel steht auf dem Blatt und nicht im Ausdruck** (R341). Ohne
          Eintrag bewertet die Kachel nichts — sie behauptet dann keine
          Beurteilung, die niemand vorgenommen hat.
        */}
        {entwurf.art !== 'stand' && (
          <fieldset className="feld">
            <legend>{t('kachel.feld.schwellen')}</legend>
            <label className="wahl">
              <span>{t('kachel.schwelle.gruen')}</span>
              <input
                type="number"
                value={entwurf.schwellen.gruenAb ?? ''}
                onChange={(e) =>
                  aendere({
                    schwellen: {
                      ...entwurf.schwellen,
                      gruenAb: e.target.value === '' ? null : Number(e.target.value),
                    },
                  })
                }
              />
            </label>
            <label className="wahl">
              <span>{t('kachel.schwelle.gelb')}</span>
              <input
                type="number"
                value={entwurf.schwellen.gelbAb ?? ''}
                onChange={(e) =>
                  aendere({
                    schwellen: {
                      ...entwurf.schwellen,
                      gelbAb: e.target.value === '' ? null : Number(e.target.value),
                    },
                  })
                }
              />
            </label>
            <label className="wahl">
              <span>{t('kachel.schwelle.richtung')}</span>
              <select
                value={entwurf.schwellen.richtung}
                onChange={(e) =>
                  aendere({
                    schwellen: {
                      ...entwurf.schwellen,
                      richtung: e.target.value as 'hoch' | 'niedrig',
                    },
                  })
                }
              >
                <option value="hoch">{t('kachel.schwelle.hoch')}</option>
                <option value="niedrig">{t('kachel.schwelle.niedrig')}</option>
              </select>
            </label>
            <small>{t('kachel.schwelle.ohne')}</small>
          </fieldset>
        )}

        <fieldset className="feld">
          <legend>{t('kachel.feld.herkunft')}</legend>
          <label className="wahl">
            <input
              type="radio"
              checked={entwurf.herkunft.folgtKontext}
              onChange={() => aendere({ herkunft: { folgtKontext: true } })}
            />
            {t('kachel.herkunft.folgt')}
          </label>
          <label className="wahl">
            <input
              type="radio"
              checked={!entwurf.herkunft.folgtKontext}
              onChange={() =>
                aendere({
                  herkunft: {
                    folgtKontext: false,
                    kreis: kontext.kreis,
                    ledger: kontext.ledger,
                    periode: kontext.periode,
                  },
                })
              }
            />
            {t('kachel.herkunft.fest')}
          </label>

          {!entwurf.herkunft.folgtKontext && (
            <div className="festgezurrt">
              {/*
                **Die beiden Felder tragen jetzt einen Namen** (R454). Solange
                sie eine Klappliste waren, lasen Prüfung und Bildschirmleser die
                Beschriftung aus den Einträgen; ein Feld, in das man tippt, hat
                keine — also steht der Name daran, wo er hingehört.
              */}
              <Auswahlfeld
                wert={entwurf.herkunft.kreis}
                beschriftung={t('kontext.kreis')}
                eintraege={(auswahl?.kreise ?? []).map(ausStammdatum)}
                aufWahl={(gewaehlt) =>
                  aendere({
                    herkunft: { ...(entwurf.herkunft as { folgtKontext: false; kreis: string; ledger: string; periode: number }), kreis: gewaehlt },
                  })
                }
              />

              <Auswahlfeld
                wert={entwurf.herkunft.ledger}
                beschriftung={t('kontext.ledger')}
                eintraege={(auswahl?.ledger ?? []).map(ausStammdatum)}
                aufWahl={(gewaehlt) =>
                  aendere({
                    herkunft: { ...(entwurf.herkunft as { folgtKontext: false; kreis: string; ledger: string; periode: number }), ledger: gewaehlt },
                  })
                }
              />

              <select
                value={entwurf.herkunft.periode}
                onChange={(e) =>
                  aendere({
                    herkunft: { ...(entwurf.herkunft as { folgtKontext: false; kreis: string; ledger: string; periode: number }), periode: Number(e.target.value) },
                  })
                }
              >
                {laufperioden.map((p) => (
                  <option key={p} value={p}>
                    {periodenname(p)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </fieldset>

        <div className="masse">
          <label className="feld">
            <span>{t('kachel.feld.breite')}</span>
            <select
              value={entwurf.breite}
              onChange={(e) => aendere({ breite: Number(e.target.value) as Breite })}
            >
              {BREITEN.map((b) => (
                <option key={b} value={b}>
                  {t(`kachel.breite.${b}` as Sprachschluessel)}
                </option>
              ))}
            </select>
          </label>

          <label className="feld">
            <span>{t('kachel.feld.hoehe')}</span>
            <select
              value={entwurf.hoehe}
              onChange={(e) => aendere({ hoehe: Number(e.target.value) as 1 | 2 })}
            >
              <option value={1}>{t('kachel.hoehe.1')}</option>
              <option value={2}>{t('kachel.hoehe.2')}</option>
            </select>
          </label>
        </div>
      </div>
    </Dialogfenster>
  )
}

/* --- Der Formeleditor ------------------------------------------------------ */

/**
 * Das Formelfeld (R342).
 *
 * **Es prüft beim Tippen und nennt jeden Befund in Fachsprache** — kein Code,
 * keine Zeichenstelle. Daneben stehen die verfügbaren Bezüge zum Einsetzen:
 * Wer den Schlüssel einer Position auswendig können muss, schreibt keine
 * Formel, sondern gibt auf.
 */
function Formelfeld({
  ausdruck,
  rolle,
  raum,
  aufAusdruck,
}: {
  ausdruck: string
  rolle: string
  raum: Kachelraum | null
  aufAusdruck: (ausdruck: string) => void
}) {
  const befund = useMemo(() => pruefeFormel(ausdruck, raum), [ausdruck, raum])

  return (
    <div className="formelfeld">
      <label className="feld">
        <span>{t('kachel.feld.formel')}</span>
        <textarea
          value={ausdruck}
          rows={2}
          spellCheck={false}
          onChange={(e) => aufAusdruck(e.target.value)}
        />
        <small>{t('kachel.formel.hinweis')}</small>
      </label>

      {befund ? <Kachelbefundzeile fehler={befund} /> : <p className="formelsteht">{t('kachel.formel.steht')}</p>}

      <p className="anzeigehinweis">
        <strong>{t('kachel.formel.anzeige')}</strong> {t('kachel.formel.anzeige.erklaerung')}
      </p>

      <details className="bezugsliste">
        <summary>{t('kachel.formel.bezuege')}</summary>
        <ul>
          {[...groessenFuer(rolle).map((g) => ({ schluessel: g.schluessel, name: t(g.schriftzug) })), ...positionsbezuege(raum)].map(
            (b) => (
              <li key={b.schluessel}>
                <button
                  type="button"
                  className="schmal"
                  title={t('kachel.formel.einsetzen')}
                  onClick={() => aufAusdruck(`${ausdruck}[${b.schluessel}]`)}
                >
                  {b.name} <code>[{b.schluessel}]</code>
                </button>
              </li>
            ),
          )}
        </ul>
      </details>
    </div>
  )
}

function pruefeFormel(ausdruck: string, raum: Kachelraum | null): Formelbefund | null {
  if (!ausdruck.trim()) {
    return { schluessel: 'formel.leer' }
  }

  const lesung = lies(ausdruck)
  if (istFehler(lesung)) {
    return lesung.fehler
  }

  if (!raum) {
    return null
  }

  const ergebnis = werteAus(lesung.knoten, formelbezuege(raum))
  return istAuswertungsfehler(ergebnis) ? ergebnis.fehler : null
}

/* --- Kleinkram, das an einer Stelle stehen soll ---------------------------- */

function formelbezuege(raum: Kachelraum): Bezugsraum {
  const raus: Bezugsraum = {}
  for (const [schluessel, b] of Object.entries(raum.bezuege)) {
    raus[schluessel] = { schluessel, name: b.name, wert: b.wert, waehrung: b.waehrung }
  }

  return raus
}

/**
 * Die Positionen in der Auswahl der Größe — **beim Namen, Gleichnamige mit
 * ihrem Bericht** (7.4 Abschnitt 34.10, R1459). Bis zum 2026-10-09 stand
 * hier „Anlagevermögen (bilanz.A)"; den Schlüssel nennt die Formelhilfe.
 */
export function positionswahl(raum: Kachelraum | null): { wert: string; text: string }[] {
  const bezuege = positionsbezuege(raum)

  return bezuege.map((b) => ({
    wert: b.schluessel,
    text:
      bezuege.filter((x) => x.name === b.name).length > 1 && b.bericht
        ? `${b.name} — ${b.bericht === 'bilanz' ? t('kachel.bericht.bilanz') : t('kachel.bericht.guv')}`
        : b.name,
  }))
}

function positionsbezuege(
  raum: Kachelraum | null,
): { schluessel: string; name: string; bericht: 'bilanz' | 'guv' | null }[] {
  return Object.values(raum?.bezuege ?? {})
    .filter((b) => b.nachweis !== null)
    .map((b) => ({ schluessel: b.schluessel, name: b.name, bericht: b.nachweis?.art ?? null }))
}

function bezugseinheit(schluessel: string, raum: Kachelraum | null): Einheitsart {
  return raum?.bezuege[schluessel]?.einheit ?? 'betrag'
}

/**
 * Wie eine Kachel heißt, solange niemand ihr einen Namen gegeben hat.
 *
 * **Der Bezugsraum wird zuerst gefragt** (R340): Eine Kachel auf der Position
 * `bilanz.A` heißt „Anlagevermögen" und nicht `bilanz.A` — der Schlüssel ist
 * die Bedeutung, der Name die Beschriftung, und am Bildschirm steht die
 * Beschriftung. Erst wenn es die Position nicht mehr gibt, steht der
 * Schlüssel da: Dann ist er die ehrlichere Auskunft.
 */
function vorratsname(kachel: Kachel, raum: Kachelraum | null = null): string {
  if (kachel.art !== 'stand' && kachel.quelle.art === 'gestellt') {
    const bezug = raum?.bezuege[kachel.quelle.schluessel]
    if (bezug) {
      return bezug.name
    }
  }

  return vorratsnameOhneRaum(kachel)
}

function vorratsnameOhneRaum(kachel: Kachel): string {
  if (kachel.art === 'stand') {
    return t(`kachel.gegenstand.${kachel.gegenstand ?? 'schritte'}` as Sprachschluessel)
  }

  if (kachel.quelle.art === 'formel') {
    return t('kachel.vorrat.formel')
  }

  const schluessel = kachel.quelle.schluessel
  const groesse = findeGroesse(schluessel)
  if (groesse) {
    return t(groesse.schriftzug)
  }

  // **Ohne Bezugsraum fragt die Kachel den Katalog** (R1220): Solange die
  // Zahlen noch geholt werden, stand hier „bilanz.POS-A" statt
  // „A. Anlagevermögen" — gefunden am 2026-10-06, als der Testlauf anfing,
  // auch Überschriften zu lesen. Die Position steht hinter dem Punkt.
  return positionsbezeichnung(schluessel.slice(schluessel.indexOf('.') + 1))
}

export function herkunftstext(kachel: Kachel, kontext: Brettkontext): string {
  /* **Der Name, nicht der Schlüssel** (R705) — aus der Auswahl, sonst aus dem
     Katalog. Ohne ihn — etwa bei einer festgezurrten Kachel auf einen Kreis,
     der nicht mehr in der Auswahl steht — bleibt der Schlüssel stehen, damit
     die Zeile nicht leer ist (R238).

     **Folgt die Kachel der Auswahl, fehlt ein Teil lieber, als dass sein
     Schlüssel dasteht.** Gleich nach dem Laden kennen weder Auswahl noch
     Katalog die Namen; am 2026-10-07 stand so auf der Startseite
     „LDG-HGB · keine Periode gewählt — folgt der Auswahl“ (Fund des
     Wächters). Einen Augenblick später ist der Name da. */
  const wort = (schluessel: string, katalogname: (s: string) => string) =>
    kontext.namen?.[schluessel] || katalogname(schluessel)
  const bekannt = (schluessel: string, katalogname: (s: string) => string) => {
    const name = wort(schluessel, katalogname)
    return name === schluessel ? '' : name
  }

  const teile = kachel.herkunft.folgtKontext
    ? [bekannt(kontext.kreis, kreisname), bekannt(kontext.ledger, bewertungsebenenname), periodenname(kontext.periode)]
    : [
        wort(kachel.herkunft.kreis, kreisname),
        wort(kachel.herkunft.ledger, bewertungsebenenname),
        periodenname(kachel.herkunft.periode),
      ]

  const satz = t(kachel.herkunft.folgtKontext ? 'kachel.herkunft.folgt.kurz' : 'kachel.herkunft.fest.kurz')

  return `${teile.filter(Boolean).join(' · ')} — ${satz}`
}

function herkunftsschluessel(kachel: Kachel, kontext: Brettkontext): string {
  return kachel.herkunft.folgtKontext
    ? `kontext:${kontext.kreis}|${kontext.ledger}|${kontext.periode}`
    : `fest:${kachel.herkunft.kreis}|${kachel.herkunft.ledger}|${kachel.herkunft.periode}`
}

function herkunftsteile(kachel: Kachel, kontext: Brettkontext): {
  kreis: string
  ledger: string
  periode: number
} {
  return kachel.herkunft.folgtKontext
    ? { kreis: kontext.kreis, ledger: kontext.ledger, periode: kontext.periode }
    : kachel.herkunft
}

/** Aus welchem Bericht eine Kachel liest — der Bezug sagt es. */
function berichtsart(kachel: Kachel): 'bilanz' | 'guv' {
  const schluessel =
    kachel.quelle.art === 'gestellt' ? kachel.quelle.schluessel : kachel.quelle.ausdruck

  return schluessel.includes('guv.') ? 'guv' : 'bilanz'
}

/* --- Die Abrufe ------------------------------------------------------------ */

/**
 * Ein Bezugsraum je Herkunft — **nicht je Kachel** (R349).
 *
 * Die Kacheln, die der Kontextzeile folgen, kosten **gar nichts**: Ihr Raum
 * entsteht aus dem, was für die Arbeitsliste ohnehin geholt wurde. Eine
 * festgezurrte Herkunft kostet einen Abruf, und zwanzig Kacheln auf derselben
 * festgezurrten Herkunft kosten denselben einen.
 */
function useRaeume(
  kacheln: readonly Kachel[],
  quellen: Brettquellen,
  kontext: Brettkontext,
): { raeume: Record<string, Kachelraum | null>; gestoert: ReadonlySet<string> } {
  const [fremde, setFremde] = useState<Record<string, Kachelraum | null>>({})
  /**
   * Die Herkünfte, deren Abruf technisch scheiterte (7.2 Abschnitt 30, R1003)
   * — sie stehen nicht als „kein Lauf" da, denn das wäre eine Behauptung.
   */
  const [gestoert, setGestoert] = useState<ReadonlySet<string>>(new Set())

  const eigene = useMemo(
    () =>
      kachelraum({
        lauf: quellen.lauf,
        urteil: quellen.urteil,
        bilanz: quellen.bilanz,
        guv: quellen.guv,
        pruefung: quellen.pruefung,
        messung: quellen.messung,
        kreis: kontext.kreis,
        ledger: kontext.ledger,
        periode: kontext.periode,
      }),
    [quellen, kontext.kreis, kontext.ledger, kontext.periode],
  )

  const gebraucht = useMemo(() => {
    const raus = new Map<string, { kreis: string; ledger: string; periode: number }>()
    for (const k of kacheln) {
      if (!k.herkunft.folgtKontext) {
        raus.set(herkunftsschluessel(k, kontext), herkunftsteile(k, kontext))
      }
    }

    return raus
  }, [kacheln, kontext])

  useEffect(() => {
    let gilt = true

    for (const [schluessel, teile] of gebraucht) {
      if (schluessel in fremde) {
        continue
      }

      api
        .aktuellerLauf(teile.kreis, teile.ledger, teile.periode)
        .then(async (lauf) => {
          if (!gilt) {
            return
          }

          if (!lauf) {
            setFremde((v) => ({ ...v, [schluessel]: null }))
            return
          }

          const [bilanz, guv, urteil] = await Promise.all([
            api.bericht(lauf.laufId, 'bilanz'),
            api.bericht(lauf.laufId, 'guv'),
            api.freigabepruefung(lauf.laufId),
          ])

          if (!gilt) {
            return
          }

          setFremde((v) => ({
            ...v,
            [schluessel]: kachelraum({
              lauf,
              urteil,
              bilanz,
              guv,
              // Nachweiskette und Antwortzeit gelten für das ganze System und
              // nicht für eine Herkunft — sie kommen aus demselben Abruf wie
              // für die Kontextzeile.
              pruefung: quellen.pruefung,
              messung: quellen.messung,
              kreis: teile.kreis,
              ledger: teile.ledger,
              periode: teile.periode,
            }),
          }))
        })
        .catch((f) => {
          if (gilt) {
            if (f instanceof Betriebsstoerung) {
              setGestoert((g) => new Set(g).add(schluessel))
            }
            setFremde((v) => ({ ...v, [schluessel]: null }))
          }
        })
    }

    return () => {
      gilt = false
    }
  }, [gebraucht, fremde, quellen.pruefung, quellen.messung])

  return {
    raeume: { ...fremde, [`kontext:${kontext.kreis}|${kontext.ledger}|${kontext.periode}`]: eigene },
    gestoert,
  }
}

/**
 * Die Punkte der Verlaufskacheln — ein Abruf je Herkunft, Raster und Bericht.
 *
 * **Kein zweiter Rechenweg** (R349): Es ist derselbe Periodenendpunkt, den das
 * Periodenbild der Berichte benutzt. Die Kachel sucht ihre Zeile heraus und
 * zeichnet, was da steht.
 */
function useVerlaeufe(kacheln: readonly Kachel[], kontext: Brettkontext): Record<string, Verlauf> {
  const [verlaeufe, setVerlaeufe] = useState<Record<string, Verlauf>>({})

  const auftraege = useMemo(
    () =>
      kacheln
        .filter((k) => k.art === 'verlauf')
        .map((k) => {
          const teile = herkunftsteile(k, kontext)
          return {
            id: k.id,
            art: berichtsart(k),
            kreis: teile.kreis,
            ledger: teile.ledger,
            bis: teile.periode,
            von: frueher(teile.periode, kontext.raster, k.einheiten),
            raster: kontext.raster,
            quelle: k.quelle,
          }
        }),
    [kacheln, kontext],
  )

  const kennung = JSON.stringify(auftraege)

  useEffect(() => {
    let gilt = true

    Promise.all(
      auftraege.map(async (a): Promise<[string, Verlauf]> => {
        if (!a.kreis || !a.ledger || !a.bis) {
          return [a.id, { art: 'befund', fehler: { schluessel: 'kachel.keinlauf' } }]
        }

        try {
          const bericht = await api.periodenbericht(a.kreis, a.ledger, a.art, a.raster, a.bis, a.von)
          return [a.id, punkteAus(bericht.spalten, bericht.zeilen, a.quelle, bericht.waehrung)]
        } catch (f) {
          // Eine Störung ist kein fehlender Lauf (7.2 Abschnitt 30, R1003):
          // Sie steht im Störungsfenster, die Kachel behauptet nichts.
          return [
            a.id,
            f instanceof Betriebsstoerung ? { art: 'gestoert' } : { art: 'befund', fehler: { schluessel: 'kachel.keinlauf' } },
          ]
        }
      }),
    ).then((paare) => {
      if (gilt) {
        setVerlaeufe(Object.fromEntries(paare))
      }
    })

    return () => {
      gilt = false
    }
    // `kennung` statt `auftraege`: Die Liste wird bei jedem Rendern neu
    // gebaut, ihr Inhalt ändert sich aber selten — sonst liefe der Abruf in
    // einer Schleife.

  }, [kennung])

  return verlaeufe
}

/**
 * Die Punkte einer Verlaufskachel — aus einer Position oder aus einer Formel.
 *
 * **Eine Formel über die Zeitachse rechnet je Periode neu** — mit den
 * Positionen genau dieser Periode. Was keine Position ist (die Zahl der
 * harten Befunde etwa), gibt es je Periode nicht, und die Kachel sagt das,
 * statt zwölfmal dieselbe Zahl zu zeichnen.
 */
function punkteAus(
  spalten: readonly { periode: number }[],
  zeilen: readonly { position: string; namen: Record<string, string>; werte: number[] }[],
  quelle: Kachel['quelle'],
  waehrung: string,
): Verlauf {
  if (quelle.art === 'gestellt') {
    const position = quelle.schluessel.replace(/^(bilanz|guv)\./, '')
    const punkte = verlaufspunkte(spalten, zeilen, position)

    return punkte === null
      ? { art: 'befund', fehler: { schluessel: 'kachel.verlauf.ohne' } }
      : { art: 'punkte', punkte, waehrung }
  }

  const lesung = lies(quelle.ausdruck)
  if (istFehler(lesung)) {
    return { art: 'befund', fehler: lesung.fehler }
  }

  const bezuege = bezuegeIn(lesung.knoten)
  const fremd = bezuege.find((b) => !/^(bilanz|guv)\./.test(b))
  if (fremd) {
    return { art: 'befund', fehler: { schluessel: 'formel.verlauf.gestellt', werte: { bezug: fremd } } }
  }

  const punkte: { periode: number; wert: number }[] = []

  for (const [i, spalte] of spalten.entries()) {
    const raum: Bezugsraum = {}
    for (const b of bezuege) {
      const position = b.replace(/^(bilanz|guv)\./, '')
      const zeile = zeilen.find((z) => z.position === position)
      if (!zeile) {
        return { art: 'befund', fehler: { schluessel: 'formel.bezug.unbekannt', werte: { bezug: b } } }
      }

      raum[b] = { schluessel: b, name: beschriftung(zeile.namen) || position, wert: zeile.werte[i] ?? 0, waehrung }
    }

    const ergebnis = werteAus(lesung.knoten, raum)
    if (istAuswertungsfehler(ergebnis)) {
      return { art: 'befund', fehler: ergebnis.fehler }
    }

    punkte.push({ periode: spalte.periode, wert: ergebnis.wert })
  }

  // Eine Formel über Beträge liefert oft eine Quote — die Währung stünde dann
  // falsch daneben. Sie steht deshalb nur an Kacheln, die Beträge zeigen; die
  // Zahlenart entscheidet das Blatt.
  return { art: 'punkte', punkte, waehrung }
}

/**
 * Die Importläufe — nur, wenn eine Kachel den Lieferstand zeigt. Ein
 * Hauptabruf (7.2 Abschnitt 39, R1211): Aus ihnen sagt die Kachel, wer
 * geliefert hat. Bis zum 2026-10-06 wurde ein Ausfall zur leeren Liste.
 */
function useLieferungen(kacheln: readonly Kachel[]): Abruf<Importlaufkopf[]> {
  const gebraucht = kacheln.some((k) => k.art === 'stand' && k.gegenstand === 'lieferstand')
  return useAbruf(gebraucht ? () => api.importlaeufe(200) : null, [gebraucht])
}

